<template>
  <div>
    <v-dialog
      v-model="isDialogOpen"
      persistent
      class="download-dialog"
      width="unset"
    >
      <v-card width="mx-auto" class="download-card">
        <v-card-title class="headline text-center">
          {{
            bundleGenerationStatus ? bundleGenerationStatus : 'Unknown status'
          }}
        </v-card-title>
        <v-card-text class="text-center">
          <v-progress-linear
            v-if="downloadProgress !== null"
            :value="downloadProgress"
            class="mt-4"
            height="25"
            color="primary"
          >
            <template v-slot:default="{ value }">
              <strong>
                {{
                  `${bytesToGB(downloadFileSize * (value / 100))}GB / ${bytesToGB(
                    downloadFileSize,
                  )}GB`
                }}
              </strong>
            </template>
          </v-progress-linear>
          <div class="d-flex flex-column" v-else-if="isDownloadReady">
            <div>
              {{
                `Your release bundle is ready for download (${bytesToGB(
                  downloadFileSize,
                )} GB)`
              }}
            </div>
            <div class="mt-2 d-flex flex-row justify-space-between">
              <v-btn @click="cancelReleaseDownload">Cancel</v-btn>
              <v-btn color="primary" @click="downloadRelease">Download</v-btn>
            </div>
          </div>
          <v-progress-circular
            v-else
            indeterminate
            color="primary"
            :size="100"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <Popup
      :show="showErrorMessage"
      :text="errorMessage"
      title="Error"
      type="error"
      @close="showErrorMessage = false"
    />
  </div>
</template>

<script>
import {
  GET_BUNDLE_GENERATION_STATUS,
  INITIATE_RELEASE_BUNDLE_BUILD_DOWNLOAD,
} from '../../graphql/upgradePackageQueries';
import { tokenAuthenticator } from '../../tokenAuthenticator';
import getEnv from '@/env-util';

export default {
  components: {
    Popup: () => import('../common/Popup'),
  },
  apollo: {
    $client: 'upgradePackageClient',
  },
  data() {
    return {
      isDialogOpen: false,
      activeReleaseBundleBuildId: null,
      downloadFileSize: null,
      bundleGenerationId: null,
      downloadProgress: null,
      isDownloadReady: false,
      bundleGenerationStatus: null,
      statusPollingIntervalId: null,
      showErrorMessage: false,
      errorMessage: '',
    };
  },
  beforeDestroy() {
    this.clearStatusPolling();
  },
  methods: {
    emitDownloadState() {
      this.$emit('download-state-change', this.activeReleaseBundleBuildId);
    },
    displayError(message) {
      this.errorMessage = message;
      this.showErrorMessage = true;
    },
    clearStatusPolling() {
      if (this.statusPollingIntervalId) {
        clearInterval(this.statusPollingIntervalId);
        this.statusPollingIntervalId = null;
      }
    },
    resetDownloadState() {
      this.clearStatusPolling();
      this.activeReleaseBundleBuildId = null;
      this.downloadFileSize = null;
      this.bundleGenerationId = null;
      this.downloadProgress = null;
      this.isDownloadReady = false;
      this.bundleGenerationStatus = null;
      this.emitDownloadState();
    },
    async initiateReleaseDownload(releaseBundleBuildId, ownerId = undefined) {
      try {
        this.clearStatusPolling();
        this.activeReleaseBundleBuildId = releaseBundleBuildId;
        this.downloadFileSize = null;
        this.bundleGenerationId = null;
        this.downloadProgress = null;
        this.isDownloadReady = false;
        this.bundleGenerationStatus = 'Starting bundle generation...';
        this.isDialogOpen = true;
        this.emitDownloadState();

        const res = await this.$apollo.mutate({
          mutation: INITIATE_RELEASE_BUNDLE_BUILD_DOWNLOAD,
          fetchPolicy: 'no-cache',
          variables: {
            releaseBundleBuildId,
            ownerId,
          },
        });

        console.log(res);

        const id = res.data.initiateReleaseBundleBuildDownload;

        if (!id) {
          this.displayError('Failed to generate release bundle');
          this.isDialogOpen = false;
          this.resetDownloadState();
          return;
        }

        this.statusPollingIntervalId = setInterval(async () => {
          const finished = await this.getBundleGenerationStatus(id);
          if (finished) {
            this.clearStatusPolling();
            this.isDownloadReady = true;
          }
        }, 5000);
      } catch (error) {
        console.error(error);
        this.displayError('Failed to generate release bundle');
        this.isDialogOpen = false;
        this.resetDownloadState();
      }
    },
    async getBundleGenerationStatus(bundleGenerationId) {
      try {
        const res = await this.$apollo.query({
          query: GET_BUNDLE_GENERATION_STATUS,
          fetchPolicy: 'no-cache',
          variables: {
            bundleGenerationId,
          },
        });

        const status = res.data.getBundleGenerationStatus;

        if (!status) {
          this.displayError('Failed to get bundle generation status');
          return false;
        }

        if (status.result === 'PENDING') {
          this.bundleGenerationStatus = status.details;
          return false;
        }

        if (status.result === 'DONE') {
          this.bundleGenerationStatus = status.details;
          this.bundleGenerationId = status.bundleGenerationId;
          this.downloadFileSize = status.fileSizeInBytes;
          return true;
        }

        this.displayError(
          'Failed to generate release bundle: ' + status.details,
        );
        return false;
      } catch (error) {
        const statusCode = error?.networkError?.statusCode;
        if (statusCode === 502) {
          // This may happen during backend bundle generation when continiously polling, and can be safely ignored
          console.warn(
            'Transient 502 during status polling, retrying...',
            error,
          );
          return false;
        }

        console.error(error);
        this.displayError('Failed to get bundle generation status');
        return false;
      }
    },
    bytesToGB(bytes) {
      if (!bytes) return 0;
      return (bytes / 1073741824).toFixed(2);
    },
    cancelReleaseDownload() {
      this.isDialogOpen = false;
      this.resetDownloadState();
    },
    async downloadRelease() {
      this.isDownloadReady = false;
      this.downloadProgress = 0;
      this.bundleGenerationStatus = 'Downloading release bundle...';

      if (!this.bundleGenerationId) {
        this.displayError(
          'Failed to download release bundle: No bundle generation ID',
        );
        return;
      }

      try {
        const response = await fetch(
          getEnv('VUE_APP_UPGRADE_PACKAGE_URL') +
            '/download-release-bundle/' +
            this.bundleGenerationId,
          {
            headers: {
              Authorization: `Bearer ${tokenAuthenticator.getToken()}`,
            },
          },
        );

        if (!response.ok) {
          console.error('Response status is: ', response.status);
          this.displayError('Failed to download release');
          return;
        }

        const contentDisposition = response.headers.get('Content-Disposition');

        if (!contentDisposition) {
          console.error('Content-Disposition header is missing');
          this.displayError(
            'Something went wrong when downloading release bundle',
          );
          return;
        }

        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);

        if (matches == null || !matches[1]) {
          console.error('No filename found in the Content-Disposition header');
          this.displayError(
            'Something went wrong when downloading release bundle',
          );
          return;
        }

        const filename = matches[1].replace(/['"]/g, '');

        const reader = response.body.getReader();
        const contentLength = Number(response.headers.get('Content-Length'));
        let receivedLength = 0;
        const chunks = [];

        const progressInterval = setInterval(() => {
          const currentProgress = Math.floor(
            (receivedLength / contentLength) * 100,
          );
          this.downloadProgress = currentProgress;
        }, 1000);

        let isReading = true;
        while (isReading) {
          const { done, value } = await reader.read();

          if (done) {
            isReading = false;
            break;
          }

          chunks.push(value);
          receivedLength += value.length;
        }

        clearInterval(progressInterval);
        this.downloadProgress = 100;

        const fileData = new Uint8Array(receivedLength);
        let position = 0;
        for (const chunk of chunks) {
          fileData.set(chunk, position);
          position += chunk.length;
        }

        const url = window.URL.createObjectURL(new Blob([fileData]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        console.error(error);
        this.displayError('Failed to download release bundle');
      } finally {
        this.cancelReleaseDownload();
      }
    },
  },
};
</script>

<style scoped>
.download-dialog {
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 450px;
  width: 450px;
}

.download-card {
  overflow: hidden;
}
</style>
